/**
 * Why a request failed, and the sentence a user is shown about it.
 *
 * Two halves, deliberately in one file:
 *
 *  · `ApiError` — the ONE error the request layer throws (`lib/queryClient.ts`).
 *    It says WHY, not merely that: `kind` separates "the server answered no"
 *    (`http`) from "nothing answered at all" (`network`) and "something answered
 *    too slowly" (`timeout`). Every failure used to arrive as a bare `Error`
 *    carrying whatever text happened to be to hand, so nothing downstream could
 *    tell a dropped connection from a refusal — and the browser's own wording
 *    ("Failed to fetch", "Load failed") reached toasts verbatim.
 *
 *  · `describeApiError` — the ONE place a failure becomes a sentence. Screens
 *    call it instead of phrasing these themselves; that is how "Couldn't reach
 *    SaleFish — check your connection" says the same thing on every screen.
 *
 * `parseApiError` predates both and is kept: ~95 call sites and every form that
 * marks a field from `fieldErrors` read it. It now understands `ApiError` too.
 */

import { msg, type Msg, type MsgVars } from "@shared/i18n";
import { localizeServerText } from "@shared/serverText";

export type ApiErrorKind = "http" | "network" | "timeout";

export class ApiError extends Error {
  /** HTTP status. 0 when nothing answered (network / timeout). */
  readonly status: number;
  /** The server's machine-readable code (`2FA_REQUIRED`, `DUPLICATE_CONTACT`), or "". */
  readonly code: string;
  readonly kind: ApiErrorKind;
  /**
   * `"<status>: <body>"` — the wire format, for `parseApiError` to recover
   * `fieldErrors` and route-specific extras from. For a network failure it is
   * the engine's own text, kept for diagnostics and never shown.
   */
  readonly raw: string;

  constructor(
    message: string,
    opts: { status?: number; code?: string; kind?: ApiErrorKind; raw?: string } = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.status = opts.status ?? 0;
    this.code = opts.code ?? "";
    this.kind = opts.kind ?? (this.status ? "http" : "network");
    this.raw = opts.raw ?? (this.status ? `${this.status}: ${message}` : message);
  }
}

/** Nothing answered — offline, DNS, a dropped link, or our own deadline. */
export function isUnreachable(e: unknown): boolean {
  if (e instanceof ApiError) return e.kind === "network" || e.kind === "timeout";
  return e instanceof Error && isNetworkFailure(e.message);
}

/**
 * Not signed in — and only that. A 429 or a 500 from an auth probe says nothing
 * about the session and must never be read as one; treating "couldn't ask" as
 * "not signed in" is what sent signed-in people to the login page on a bad
 * connection.
 */
export function isUnauthenticated(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401;
}

export interface ParsedApiError {
  /** HTTP status, when the error came from a response. */
  status?: number;
  /** The server's stable error code, e.g. `DUPLICATE_CONTACT`. */
  code?: string;
  /** A sentence fit to show a user. Never raw JSON. */
  message: string;
  /** Field path → message, for marking inputs. Empty when the server sent none. */
  fieldErrors: Record<string, string>;
  /** Extra values the route attached (e.g. `contactId` on a duplicate). */
  data: Record<string, unknown>;
}

const STATUS_PREFIX = /^(\d{3}):\s*/;

/**
 * What each engine says when the request never left the machine. None of them
 * is a sentence a user can act on, and all of them mean the same thing.
 */
const NETWORK_FAILURES = [
  "failed to fetch",                                  // Chrome / Edge
  "networkerror when attempting to fetch resource",   // Firefox
  "load failed",                                      // Safari
  "the network connection was lost",                  // Safari
  "network request failed",
];

/*
  The sentences, as translatable descriptors. The `*_MESSAGE` strings below are
  their English and stay plain strings, because ~95 call sites compare, join
  and toast them as text; a screen that wants the reader's language passes the
  sentence through `localizeApiMessage` where it renders it.
*/
const UNREACHABLE_MSG = msg("shellErrorUnreachable", "Couldn't reach SaleFish — check your connection and try again.");
const TIMEOUT_MSG = msg("shellErrorTimeout", "SaleFish took too long to respond — check your connection and try again.");
const RATE_LIMITED_MSG = msg("shellErrorRateLimited", "Too many requests from this network — wait a minute and try again.");
const SIGNED_OUT_MSG = msg("shellErrorSignedOut", "Your session has ended. Sign in again to continue.");
const FORBIDDEN_MSG = msg("shellErrorForbidden", "You don't have access to that.");
const SERVER_FAULT_MSG = msg("shellErrorServerFault", "SaleFish had a problem handling that — please try again.");
const GENERIC_MSG = msg("shellErrorGeneric", "Something went wrong. Please try again.");
const STEP_UP_DECLINED_MSG = msg("shellErrorStepUpDeclined", "Confirm it's you to carry on — nothing was changed.");
const SECURE_REQUEST_FAILED_MSG = msg(
  "shellErrorSecureRequestFailed",
  "Couldn't start a secure request. Refresh the page and try again.",
);

/** The sentences. One per kind of failure, used by every screen. */
export const UNREACHABLE_MESSAGE = UNREACHABLE_MSG.en;
export const TIMEOUT_MESSAGE = TIMEOUT_MSG.en;
export const RATE_LIMITED_MESSAGE = RATE_LIMITED_MSG.en;
export const SIGNED_OUT_MESSAGE = SIGNED_OUT_MSG.en;
export const FORBIDDEN_MESSAGE = FORBIDDEN_MSG.en;
export const SERVER_FAULT_MESSAGE = SERVER_FAULT_MSG.en;
export const GENERIC_MESSAGE = GENERIC_MSG.en;
/** A 2FA 403 whose in-place confirmation was declined (see lib/stepUp.ts). */
export const STEP_UP_DECLINED_MESSAGE = STEP_UP_DECLINED_MSG.en;
/** No CSRF token could be had, so a write never left (lib/queryClient.ts). */
export const SECURE_REQUEST_FAILED_MESSAGE = SECURE_REQUEST_FAILED_MSG.en;

const KNOWN_SENTENCES = new Map<string, Msg>(
  [
    UNREACHABLE_MSG,
    TIMEOUT_MSG,
    RATE_LIMITED_MSG,
    SIGNED_OUT_MSG,
    FORBIDDEN_MSG,
    SERVER_FAULT_MSG,
    GENERIC_MSG,
    STEP_UP_DECLINED_MSG,
    SECURE_REQUEST_FAILED_MSG,
  ].map((m) => [m.en, m]),
);

/**
 * One of the sentences above, or a sentence the server wrote that a catalogue
 * in shared/serverText/ knows ("A contact with this email already exists."),
 * in the reader's language; any other text (a caller's fallback, a vendor's
 * message) unchanged. Pass `t` from `useTranslation()`.
 */
export function localizeApiMessage(text: string, t: (m: Msg, vars?: MsgVars) => string): string {
  const m = KNOWN_SENTENCES.get(text);
  return m ? t(m) : localizeServerText(text, t);
}

function isNetworkFailure(message: string): boolean {
  const lower = message.trim().toLowerCase();
  return NETWORK_FAILURES.some((needle) => lower.includes(needle));
}

function coerceMessage(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/**
 * `snake_case_codes`, `UPPER_CODES`, `404 Not Found`, the error handler's
 * `Internal Server Error` — a status, not something to read.
 */
function isBareCode(m: string): boolean {
  const s = m.trim();
  return (
    /^[a-z0-9]+(_[a-z0-9]+)+$/.test(s) ||
    /^[A-Z0-9]+(_[A-Z0-9]+)*$/.test(s) ||
    /^\d{3}\b/.test(s) ||
    /^internal server error$/i.test(s) ||
    /^request failed$/i.test(s) ||
    // Framework boilerplate: true, and no help to anybody reading it.
    /^(not authenticated|unauthori[sz]ed|authentication required|forbidden|not found)\.?$/i.test(s)
  );
}

/**
 * The sentence to show a user for any failure, so no screen has to phrase these
 * itself.
 *
 * For an `http` failure the server's own sentence wins when it wrote one — "A
 * contact with this email already exists" is more use than any generic line,
 * and the terminal error handler already refuses to leak 5xx internals, so a
 * 5xx that carries a sentence carries one somebody wrote on purpose ("Email
 * service is not configured. Please contact support.").
 *
 * A 401 included. The sign-in form's refusals are 401s too — "Invalid email or
 * password.", "Account temporarily locked. Try again in 30 minutes." — and
 * answering every one with "Your session has ended" told people at the sign-in
 * form, who had no session, to sign in again, so they retried into the lockout
 * (2026-11-02). A bare "Not authenticated" still reads as the session ending.
 */
export function describeApiError(e: unknown, fallback = GENERIC_MESSAGE): string {
  if (e instanceof ApiError) {
    if (e.kind === "timeout") return TIMEOUT_MESSAGE;
    if (e.kind === "network") return UNREACHABLE_MESSAGE;
    const own = e.message && !isBareCode(e.message) ? e.message : "";
    if (e.status === 429) return RATE_LIMITED_MESSAGE;
    if (e.status === 401) return own || SIGNED_OUT_MESSAGE;
    if (e.status === 403) return own || FORBIDDEN_MESSAGE;
    if (e.status >= 500) return own || SERVER_FAULT_MESSAGE;
    return own || fallback;
  }
  return parseApiError(e, fallback).message;
}

/**
 * @param error  whatever landed in a `catch` / react-query `onError`
 * @param fallback  shown when the error carries nothing readable
 */
export function parseApiError(error: unknown, fallback = GENERIC_MESSAGE): ParsedApiError {
  if (error instanceof ApiError && error.kind !== "http") {
    return {
      code: error.kind === "timeout" ? "TIMEOUT" : "NETWORK",
      message: error.kind === "timeout" ? TIMEOUT_MESSAGE : UNREACHABLE_MESSAGE,
      fieldErrors: {},
      data: {},
    };
  }

  if (!(error instanceof Error)) {
    return { message: coerceMessage(error) ?? fallback, fieldErrors: {}, data: {} };
  }

  // apiRequest sets `message` to the readable half and keeps the wire format
  // (`"<status>: <body>"`) on `raw`, so ~95 toasts that render `error.message`
  // directly never show users a status code and a JSON blob. Prefer `raw` here
  // so this function still recovers `status`, `code` and `fieldErrors`; fall
  // back to `message` for errors thrown anywhere else.
  const carrier = error as unknown as { raw?: unknown };
  const raw = (typeof carrier.raw === "string" ? carrier.raw : error.message) ?? "";

  // A failed fetch has no status and no body, so it would otherwise fall
  // through everything below and surface as the engine's own wording.
  if (isNetworkFailure(raw)) {
    return { code: "NETWORK", message: UNREACHABLE_MESSAGE, fieldErrors: {}, data: {} };
  }

  const statusMatch = raw.match(STATUS_PREFIX);
  const status = statusMatch ? Number(statusMatch[1]) : undefined;
  const body = statusMatch ? raw.slice(statusMatch[0].length) : raw;

  let payload: Record<string, unknown> | undefined;
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      payload = parsed as Record<string, unknown>;
    }
  } catch {
    // Not JSON — an HTML error page or a plain string. Fall through.
  }

  if (!payload) {
    // Never show the caller a JSON blob or a bare status line.
    const looksLikeJson = body.trimStart().startsWith("{") || body.trimStart().startsWith("[");
    const looksLikeHtml = body.trimStart().startsWith("<");
    return {
      status,
      code: error instanceof ApiError && error.code ? error.code : undefined,
      message: looksLikeJson || looksLikeHtml ? fallback : coerceMessage(body) ?? fallback,
      fieldErrors: {},
      data: {},
    };
  }

  const { message, error: errorField, code: codeField, details, fieldErrors, validationErrors, ...rest } = payload;

  // `error` is a code on the routes that use one (DUPLICATE_CONTACT) and a
  // sentence on the ones that don't ("Failed to create contact"). A code has no
  // spaces; that is the only reliable way to tell them apart. Some routes send
  // the code as `code` instead (`2FA_REQUIRED`, the session-timeout codes).
  const errorString = typeof errorField === "string" ? errorField : undefined;
  const errorIsCode = Boolean(errorString && /^[A-Z0-9_]+$/.test(errorString));
  const code =
    (errorIsCode ? errorString : undefined) ??
    (typeof codeField === "string" && codeField ? codeField : undefined);

  const readable =
    coerceMessage(message) ??
    (errorIsCode ? undefined : coerceMessage(errorString)) ??
    coerceMessage(details) ??
    (Array.isArray(validationErrors) ? coerceMessage(validationErrors[0]) : undefined) ??
    fallback;

  return {
    status,
    code,
    message: readable,
    fieldErrors:
      fieldErrors && typeof fieldErrors === "object" && !Array.isArray(fieldErrors)
        ? (fieldErrors as Record<string, string>)
        : {},
    data: rest,
  };
}

/** Convenience for the common `toast({ description })` case. */
export function apiErrorMessage(error: unknown, fallback?: string): string {
  if (error instanceof ApiError) return describeApiError(error, fallback);
  return parseApiError(error, fallback).message;
}
